/**
 * Canal SSE UNIFICADO — ponte de conteúdo (Epic D, Fase D2).
 *
 * <p>North star do Epic D: o player mantém UMA conexão SSE persistente, não duas.
 * O {@code RemoteOpsService} (dono do EventSource unificado
 * {@code /api/sse/device/{id}}) recebe o evento nomeado {@code content} e
 * PUBLICA aqui; o {@code SyncService} SE INSCREVE e liga nos MESMOS handlers que
 * já usava com o Content SSE. Assim um {@code content_changed} pelo canal
 * unificado dispara EXATAMENTE o mesmo comportamento que disparava pela 2ª
 * conexão.</p>
 *
 * <p><b>Por que um módulo-folha (e não import direto entre os serviços):</b>
 * {@code RemoteOpsService} importa {@code ~/stores} e {@code SyncService} vive
 * dentro do grafo de stores. Um import direto entre os dois criaria um ciclo
 * ({@code sync → remote-ops → stores → sync}) e arrastaria o grafo pesado de
 * stores/kiosk pros testes de qualquer um. Este módulo não importa nada pesado
 * (só tipos) — desacopla os dois lados sem ciclo.</p>
 */

import type { SSEEvent } from './types';

type ContentEventListener = (event: SSEEvent) => void;

class UnifiedContentChannel {
  private readonly listeners = new Set<ContentEventListener>();

  /**
   * Inscreve um handler pros eventos de conteúdo do canal unificado.
   * Retorna a função de cancelamento (espelha {@code onContentChange}).
   */
  onContentEvent(listener: ContentEventListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Publica um evento de conteúdo já parseado pra todos os inscritos. */
  emit(event: SSEEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error(
          '[UnifiedContentChannel] erro em callback de conteúdo',
          err,
        );
      }
    });
  }

  /** true quando há ≥1 inscrito (o SyncService está consumindo o canal). */
  hasListeners(): boolean {
    return this.listeners.size > 0;
  }

  /** Só pra teste — limpa inscrições entre casos. */
  reset(): void {
    this.listeners.clear();
  }
}

/** Singleton — RemoteOpsService publica, SyncService consome. */
export const unifiedContentChannel = new UnifiedContentChannel();
